import { useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { paths } from "@shared-core/routing/routes";
import type {
  BookPickCourierResponse,
  PickDeliverTo,
  PickDespatchResult,
  SupplierPickPage,
} from "@shared-core/data/supplier-picks-adapter";
import { useSupplierPicksAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import { useLoad } from "@shared-core/domain/useLoad";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ACTION_WORDING,
  COURIER_WORDING,
  PICK_WORDING,
  STAFF_VIEW_WORDING,
  batchInstructions,
  type BatchLine,
  batchName,
  carrierAndTracking,
  count,
  dayMonth,
  destinationProgress,
  pickDestinationLabel,
  pickStatus,
  sendableTo,
  stillToGo,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Badge } from "../../app/components/primitives/badge";
import { Card } from "../../app/components/primitives/card";
import { Input } from "../../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import { StatusPill } from "../../app/components/domain/StatusPill";
import {
  StaffViewReadOnly,
  StaffViewReadOnlyNoteOnce,
} from "../../app/components/domain/StaffViewReadOnly";
import { FormField } from "../../app/components/patterns/FormField";
import { useSupplierSession } from "../shell/session";
import { useNotFoundWhy } from "../shell/staffView";
import {
  EmptyState,
  LoadFailed,
  LoadingCard,
  SectionHeading,
} from "../shell/states";
import {
  despatchEntry,
  emptyDespatchFields,
  foldDespatchErrors,
  unplacedDespatchErrors,
  type DespatchFields,
} from "./despatchFields";
import { BookCourierForm } from "./BookCourierForm";
import { PickDespatchFields } from "./PickDespatchFields";
import { PickResults } from "./PickResults";

/**
 * `/picks/:pickId` (login only; spec 14 §5.3, §8.2): one pick for the person standing at the shelf. The line
 * reference (what we quote to each other), the item, the batch to take, every destination in full with what has
 * already gone, and, while the pick is open, *Record despatch* for one destination at a time (despatch is per
 * delivery line, spec 14 §10.1). Where the server books a courier, *Book with DPD / DX* sits above it (courier
 * chain): the booking records the despatch and its label opens at once, with *Print label again* beside the
 * notice. The server decides what is allowed (`actions`); a pick that was cancelled while the page was open says
 * so when the despatch is refused and the page re-reads.
 */
export function PickPage() {
  const { pickId = "" } = useParams();
  const picks = useSupplierPicksAdapter();
  const sessionState = useSupplierSession();
  const location = useLocation();
  const read = useLoad(() => picks.getPick(pickId), pickId);
  const notFoundWhy = useNotFoundWhy("pick", PICK_WORDING.notFoundWhy);
  const [results, setResults] = useState<{
    results: PickDespatchResult[];
    lineRefs: Record<string, string>;
  } | null>(null);
  const [booked, setBooked] = useState<BookPickCourierResponse | null>(null);
  const [labelFailed, setLabelFailed] = useState(false);
  const capabilities = usePlatformCapabilities();
  const printLabel = async (labelUrl: string) => {
    setLabelFailed(false);
    try {
      capabilities.openBlobInNewTab(
        await picks.downloadPickCourierLabel(labelUrl),
      );
    } catch {
      setLabelFailed(true);
    }
  };

  const listLink = (
    <Link
      to={paths.supplierPicks()}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft aria-hidden className="size-4" />
      Picks
    </Link>
  );

  if (read.error && !read.data) {
    return (
      <div className="grid gap-4">
        {listLink}
        <LoadFailed
          what="this pick"
          message={mutationErrorMessage(
            read.error,
            "Something went wrong reaching Pollards.",
          )}
          onRetry={read.reload}
        />
      </div>
    );
  }
  if (!read.data) {
    return (
      <div className="grid gap-4">
        {listLink}
        <LoadingCard what="this pick" />
      </div>
    );
  }
  if (read.data.kind === "not_found") {
    return (
      <div className="grid gap-4">
        {listLink}
        <Card>
          <EmptyState title={PICK_WORDING.notFoundTitle}>
            <p>{notFoundWhy}</p>
          </EmptyState>
        </Card>
      </div>
    );
  }
  if (read.data.kind === "signed_out") {
    return (
      <Card>
        <EmptyState
          title={PICK_WORDING.signedOutTitle}
          action={
            <Button asChild>
              <Link
                to={paths.supplierSignIn({
                  returnTo: `${location.pathname}${location.search}`,
                })}
              >
                {ACTION_WORDING.signIn}
              </Link>
            </Button>
          }
        />
      </Card>
    );
  }

  const pick = read.data.page;
  const status = pickStatus(pick);
  const batches = batchInstructions(pick.suggested_batches, pick.quantity);

  return (
    <div className="grid gap-4">
      <Link
        to={paths.supplierPicks({ warehouseId: pick.warehouse_id })}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft aria-hidden className="size-4" />
        Picks at {pick.warehouse_name}
      </Link>

      <Card className="gap-1 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-lg font-semibold">{pick.line_ref}</h1>
          <StatusPill label={status.label} tone={status.tone} />
        </div>
        <p className="text-sm text-muted-foreground">
          <span className="font-mono">{pick.stock_code}</span> ·{" "}
          {pick.description}
        </p>
      </Card>

      {pick.state === "cancelled" && (
        <p
          role="alert"
          className="rounded-lg border bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200"
        >
          {PICK_WORDING.cancelledBanner}
        </p>
      )}

      {booked && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-3 text-sm"
        >
          <div>
            <p>
              {COURIER_WORDING.booked(
                booked.carrier.toUpperCase(),
                booked.consignment_number,
                booked.is_repeat,
              )}
            </p>
            {labelFailed && (
              <p className="text-red-600">{COURIER_WORDING.labelFailed}</p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void printLabel(booked.label_url)}
          >
            {COURIER_WORDING.printAgain}
          </Button>
        </div>
      )}

      {results && (
        <PickResults
          results={results.results}
          lineRefs={results.lineRefs}
          onDismiss={() => setResults(null)}
        />
      )}

      <Card className="gap-3 p-4">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Fact label="Quantity">
            {count(pick.quantity)}
            {pick.stock_parts_label && (
              <span className="block text-muted-foreground">
                {pick.stock_parts_label}
              </span>
            )}
          </Fact>
          <Fact label="Pack quantity">{count(pick.pack_quantity)}</Fact>
          {pick.size && <Fact label="Size">{pick.size}</Fact>}
          <Fact label={PICK_WORDING.warehouse}>{pick.warehouse_name}</Fact>
          <Fact label={PICK_WORDING.requiredBy}>
            {pick.required_on ? dayMonth(pick.required_on) : "—"}
          </Fact>
          <Fact label={PICK_WORDING.customerReference}>
            {pick.customer_reference ?? "—"}
          </Fact>
        </dl>
        {pick.packing_note && (
          <div className="rounded-md bg-muted/50 p-3 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase">
              {PICK_WORDING.packingNote}
            </p>
            <p>{pick.packing_note}</p>
          </div>
        )}
      </Card>

      {batches && (
        <section aria-labelledby="pick-batches" className="grid gap-2">
          <SectionHeading id="pick-batches">
            {PICK_WORDING.batchToTake}
          </SectionHeading>
          <Card className="gap-1 p-4 text-sm">
            <BatchLineView line={batches.take} className="font-semibold" />
            {batches.others.map((other) => (
              <BatchLineView
                key={other.text}
                line={other}
                className="text-muted-foreground"
              />
            ))}
          </Card>
        </section>
      )}

      <section aria-labelledby="pick-deliver-to" className="grid gap-2">
        <SectionHeading id="pick-deliver-to">
          {PICK_WORDING.deliverTo}
        </SectionHeading>
        <div className="grid gap-3 sm:grid-cols-2">
          {pick.deliver_to.map((destination) => (
            <DestinationCard
              key={destination.delivery_line_id}
              destination={destination}
            />
          ))}
        </div>
      </section>

      {pick.despatches.length > 0 && (
        <section aria-labelledby="pick-despatches" className="grid gap-2">
          <SectionHeading id="pick-despatches">
            {PICK_WORDING.despatchesSoFar}
          </SectionHeading>
          <Card className="gap-2 p-4 text-sm">
            <ul className="grid gap-2">
              {pick.despatches.map((d) => {
                const to = pick.deliver_to.find(
                  (x) => x.delivery_line_id === d.delivery_line_id,
                );
                return (
                  <li
                    key={`${d.delivery_line_id}-${d.despatch_ref}`}
                    className="flex flex-wrap gap-x-3"
                  >
                    <span className="font-mono text-xs">{d.despatch_ref}</span>
                    <span>
                      {count(d.quantity)} on {dayMonth(d.despatched_on)}
                    </span>
                    {d.carrier && (
                      <span className="text-muted-foreground">
                        {carrierAndTracking(
                          d.carrier,
                          d.carrier_name,
                          d.tracking_number,
                        )}
                      </span>
                    )}
                    {to && pick.deliver_to.length > 1 && (
                      <span className="text-muted-foreground">
                        to {to.address_name}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        </section>
      )}

      <StaffViewReadOnlyNoteOnce sentence={STAFF_VIEW_WORDING.pickDespatch} />
      {pick.actions.includes("record_despatch") && (
        <StaffViewReadOnly>
          <div className="grid gap-4">
            <BookCourierForm
              // A booking changes what is still to go: start again with a new request id.
              key={`book-${pick.deliver_to.map((d) => d.despatched_quantity).join("-")}`}
              pick={pick}
              onBooked={(booking) => {
                setBooked(booking);
                setResults(null);
                void printLabel(booking.label_url);
                read.reload();
                sessionState?.refresh();
              }}
            />
            <RecordDespatchForm
              // A despatch changes what is still to go: start the form again from the new defaults.
              key={pick.deliver_to.map((d) => d.despatched_quantity).join("-")}
              pick={pick}
              onRecorded={(recorded) => {
                setResults({
                  results: recorded,
                  lineRefs: { [pick.pick_id]: pick.line_ref },
                });
                read.reload();
                sessionState?.refresh();
              }}
            />
          </div>
        </StaffViewReadOnly>
      )}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function DestinationCard({ destination }: { destination: PickDeliverTo }) {
  const progress = destinationProgress(destination);
  return (
    <Card className="gap-1 p-4 text-sm">
      <p className="font-semibold">{destination.address_name}</p>
      {destination.address && <p>{destination.address}</p>}
      <p>
        {[destination.postcode, destination.country]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <p className="mt-1">
        <span className="font-semibold">{count(destination.quantity)}</span> to
        this address
        {progress && (
          <span className="text-muted-foreground"> · {progress}</span>
        )}
      </p>
    </Card>
  );
}

/**
 * Record despatch for one destination of the pick: where it went (a choice when the pick has several addresses,
 * limited to those with something still to go), how many (defaulting to all still to go there that the pick holds), the batch picked
 * (defaulting to the suggestion; any listed batch) and the reference, date and carrier. The form's inputs carry
 * the server's refusal next to them; a refusal of the entry itself (a cancelled pick, a batch stock won't take) is
 * a result, shown above.
 */
function RecordDespatchForm({
  pick,
  onRecorded,
}: {
  pick: SupplierPickPage;
  onRecorded: (results: PickDespatchResult[]) => void;
}) {
  const picks = useSupplierPicksAdapter();
  const open = pick.deliver_to.filter((d) => stillToGo(d) > 0);
  const [destinationId, setDestinationId] = useState(
    open[0]?.delivery_line_id ?? "",
  );
  const destination = open.find((d) => d.delivery_line_id === destinationId);
  const [quantity, setQuantity] = useState(
    String(destination ? sendableTo(pick, destination) : ""),
  );
  const [batchId, setBatchId] = useState(
    pick.suggested_batches[0]?.batch_id ?? "",
  );
  const [fields, setFields] = useState<DespatchFields>(
    emptyDespatchFields(pick.today),
  );
  const write = useFieldErrorWrite("We couldn't send this to Pollards.");
  const errors = foldDespatchErrors(write.fieldErrors);

  const submit = () =>
    void write.run(async () => {
      const recorded = await picks.recordPickDespatches([
        despatchEntry(pick.pick_id, fields, {
          delivery_line_id: destinationId || null,
          quantity: quantity.trim() === "" ? null : Number(quantity),
          batch_id: batchId || null,
        }),
      ]);
      onRecorded(recorded);
    });

  return (
    <form
      noValidate
      aria-label={PICK_WORDING.recordTitle}
      className="grid gap-3 rounded-lg border bg-card p-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <p className="text-sm font-semibold">{PICK_WORDING.recordTitle}</p>
      {write.banner && (
        <p role="alert" className="text-sm text-red-600">
          {write.banner}
        </p>
      )}
      {unplacedDespatchErrors(errors).map((message) => (
        <p key={message} className="text-sm text-red-600">
          {message}
        </p>
      ))}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {open.length > 1 && (
          <FormField
            id="record-destination"
            label={PICK_WORDING.destination}
            error={errors.delivery_line_id}
          >
            {(control) => (
              <Select
                value={destinationId}
                onValueChange={(next) => {
                  setDestinationId(next);
                  const chosen = open.find((d) => d.delivery_line_id === next);
                  setQuantity(String(chosen ? sendableTo(pick, chosen) : ""));
                }}
              >
                <SelectTrigger {...control} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {open.map((d) => (
                    <SelectItem
                      key={d.delivery_line_id}
                      value={d.delivery_line_id}
                    >
                      {pickDestinationLabel(d)} · {count(stillToGo(d))} still to
                      go
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        )}
        <FormField
          id="record-quantity"
          label={PICK_WORDING.quantity}
          hint={
            destination
              ? `${count(stillToGo(destination))} still to go${open.length > 1 ? " to this address" : ""}`
              : null
          }
          error={errors.quantity}
        >
          {(control) => (
            <Input
              {...control}
              type="number"
              inputMode="numeric"
              min={1}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          )}
        </FormField>
        {pick.suggested_batches.length > 0 && (
          <FormField
            id="record-batch"
            label={PICK_WORDING.batchPicked}
            error={errors.batch_id}
          >
            {(control) => (
              <Select value={batchId} onValueChange={setBatchId}>
                <SelectTrigger {...control} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pick.suggested_batches.map((b, index) => (
                    <SelectItem key={b.batch_id} value={b.batch_id}>
                      {batchName(b)}, received {dayMonth(b.received_on)}
                      {index === 0 ? ` (${PICK_WORDING.suggestedBatch})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        )}
      </div>
      <PickDespatchFields
        idPrefix="record"
        value={fields}
        onChange={setFields}
        errors={errors}
        today={pick.today}
      />
      <div className="flex justify-end">
        <Button type="submit" disabled={write.isBusy}>
          {ACTION_WORDING.recordDespatch}
        </Button>
      </div>
    </form>
  );
}

/** One batch's instruction with what to watch for beside it: stale print gets the warning badge. */
function BatchLineView({
  line,
  className,
}: {
  line: BatchLine;
  className: string;
}) {
  return (
    <p className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span>{line.text}</span>
      {line.notes.map((note) => (
        <Badge
          key={note}
          variant={note === PICK_WORDING.olderArtwork ? "warning" : "neutral"}
        >
          {note}
        </Badge>
      ))}
    </p>
  );
}
